import { PageHeader } from 'core/components/ui/PageHeader'
import { authApiDatasource } from 'features/auth/data/datasources/AuthApiDatasource'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React, { useState } from 'react'

export const SettingsPage: React.FC = () => {
  const { user } = useAuth()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [updating, setUpdating] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  const handleUpdatePassword = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrorMsg('')
    setSuccessMsg('')

    if (newPassword.length < 6) {
      setErrorMsg('Password must be at least 6 characters.')
      return
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.')
      return
    }

    if (!user?.email) {
      setErrorMsg('User email not found. Cannot reset password.')
      return
    }

    setUpdating(true)
    try {
      await authApiDatasource.resetPassword(user.email, newPassword)
      setSuccessMsg('Password updated successfully!')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Failed to update password.')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <PageHeader
        title="Settings"
        description="Settings display the information related to the account roles and responsibiltiy"
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Settings' }]}
      />

      <div className="grid grid-cols-1 gap-8">
        {/* Change Password Card */}
        <div className="border border-border p-6 bg-surface rounded-card shadow-sm space-y-4">
          <div className="border-b border-border pb-3">
            <h3 className="text-md font-bold text-text-primary">Change Password</h3>
            <p className="text-xs text-text-secondary">Update your account password.</p>
          </div>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            {errorMsg && (
              <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="p-3 text-xs bg-status-active/10 border border-status-active/20 text-status-active rounded-btn font-medium">
                {successMsg}
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-primary uppercase tracking-wider">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
                placeholder="Min 6 characters"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3.5 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
                placeholder="Re-type new password"
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={updating || !newPassword || !confirmPassword}
                className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>

        {/* Read-only Role Permissions matrix info card */}
        <div className="border border-border p-6 bg-surface rounded-card shadow-sm space-y-4">
          <div className="border-b border-border pb-3">
            <h3 className="text-md font-bold text-text-primary">Access Matrix reference</h3>
            <p className="text-xs text-text-secondary">
              Role permissions reference mapping dashboard visibility modules. Modifying these rules requires system
              redeployment.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border table-auto text-left text-xs">
              <thead className="bg-bg text-text-secondary font-bold uppercase">
                <tr>
                  <th className="px-4 py-2">Role Name</th>
                  <th className="px-4 py-2">Users</th>
                  <th className="px-4 py-2">Catalogue</th>
                  <th className="px-4 py-2">Orders</th>
                  <th className="px-4 py-2">Payments</th>
                  <th className="px-4 py-2">Grievances</th>
                  <th className="px-4 py-2">Dispatch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface text-text-primary">
                <tr>
                  <td className="px-4 py-3 font-semibold">CXO (Executive)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Manage)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Edit)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Update)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Verify)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Resolve)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Dispatch)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Supervisor (Admin)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Manage)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Edit)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Update)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Verify)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Resolve)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Dispatch)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Finance Team</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 font-bold text-primary">Verify & Confirm</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Account Manager</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Manage)</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Update)</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Resolve)</td>
                  <td className="px-4 py-3 font-bold text-primary">All (Dispatch)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Customer Support</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                  <td className="px-4 py-3 text-text-secondary">View only</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
